import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CardService, CardData } from '../services/cardService';
import { UserService, UserProfile } from '../services/userService';
import { StorageService } from '../services/storageService';
import { useTranslation } from 'react-i18next';

const FirebaseTestPage: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userCards, setUserCards] = useState<CardData[]>([]);
  const [testStatus, setTestStatus] = useState<string>('');
  const { t } = useTranslation();

  // 사용자 프로필 가져오기 테스트
  const testUserProfile = async () => {
    if (!user?.uid) return;
    
    try {
      setTestStatus('사용자 프로필 가져오는 중...');
      const profile = await UserService.getUserProfile(user.uid);
      setUserProfile(profile);
      setTestStatus('사용자 프로필 가져오기 성공!');
    } catch (error) {
      console.error('사용자 프로필 테스트 실패:', error);
      setTestStatus('사용자 프로필 가져오기 실패');
    }
  };

  // 사용자 카드 가져오기 테스트
  const testUserCards = async () => {
    if (!user?.uid) return;
    
    try {
      setTestStatus('사용자 카드 가져오는 중...');
      const cards = await CardService.getUserCards(user.uid);
      setUserCards(cards);
      setTestStatus('사용자 카드 가져오기 성공!');
    } catch (error) {
      console.error('사용자 카드 테스트 실패:', error);
      setTestStatus('사용자 카드 가져오기 실패');
    }
  };

  // 실시간 카드 구독 테스트
  const testRealTimeCards = () => {
    if (!user?.uid) return;
    
    try {
      setTestStatus('실시간 카드 구독 시작...');
      const unsubscribe = CardService.subscribeToUserCards(user.uid, (cards) => {
        setUserCards(cards);
        setTestStatus('실시간 카드 업데이트 성공!');
      });
      
      // 10초 후 구독 해제
      setTimeout(() => {
        unsubscribe();
        setTestStatus('실시간 구독 테스트 완료');
      }, 10000);
    } catch (error) {
      console.error('실시간 카드 테스트 실패:', error);
      setTestStatus('실시간 카드 구독 실패');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('common.firebaseTest', 'Firebase 테스트')}</h1>
          <p className="text-gray-600">{t('auth.loginRequired', '로그인이 필요합니다.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">{t('common.firebaseConnectionTest', 'Firebase 연결 테스트')}</h1>
        
        {/* 사용자 정보 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{t('dashboard.userInfo', '사용자 정보')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p><strong>UID:</strong> {user?.uid}</p>
              <p><strong>{t('common.email', '이메일')}:</strong> {user?.email}</p>
              <p><strong>{t('dashboard.name', '이름')}:</strong> {user?.displayName || t('dashboard.notSet', '설정되지 않음')}</p>
            </div>
            <div>
              <p><strong>{t('auth.authStatus', '인증 상태')}:</strong> {isAuthenticated ? '✅ 로그인됨' : '❌ 로그아웃됨'}</p>
              <p><strong>{t('auth.emailVerified', '이메일 인증')}:</strong> {user?.emailVerified ? '✅ 인증됨' : '❌ 미인증'}</p>
            </div>
          </div>
        </div>

        {/* 테스트 버튼들 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{t('common.firebaseTest', 'Firebase 테스트')}</h2>
          <div className="space-y-4">
            <button
              onClick={testUserProfile}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mr-4"
            >
              {t('common.userProfileTest', '사용자 프로필 테스트')}
            </button>
            <button
              onClick={testUserCards}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded mr-4"
            >
              {t('common.userCardsTest', '사용자 카드 테스트')}
            </button>
            <button
              onClick={testRealTimeCards}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded"
            >
              {t('common.realtimeCardsTest', '실시간 카드 구독 테스트')}
            </button>
          </div>
          {testStatus && (
            <div className="mt-4 p-3 bg-gray-100 rounded">
              <p className="text-sm text-gray-700">{testStatus}</p>
            </div>
          )}
        </div>

        {/* 사용자 프로필 결과 */}
        {userProfile && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">{t('dashboard.userProfile', '사용자 프로필')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p><strong>{t('dashboard.plan', '플랜')}:</strong> {userProfile.plan || t('dashboard.notSet', '설정되지 않음')}</p>
                <p><strong>{t('dashboard.cardCount', '카드 수')}:</strong> {userProfile.cardCount || 0}</p>
                <p><strong>{t('dashboard.maxCards', '최대 카드')}:</strong> {userProfile.maxCards || t('dashboard.unlimited', '무제한')}</p>
              </div>
              <div>
                <p><strong>{t('dashboard.trial', '트라이얼 활성')}:</strong> {userProfile.isTrialActive ? '✅ 활성' : '❌ 비활성'}</p>
                <p><strong>{t('common.createdAt', '생성일')}:</strong> {userProfile.createdAt?.toDate?.()?.toLocaleString() || t('common.unknown', '알 수 없음')}</p>
                <p><strong>{t('common.updatedAt', '업데이트일')}:</strong> {userProfile.updatedAt?.toDate?.()?.toLocaleString() || t('common.unknown', '알 수 없음')}</p>
              </div>
            </div>
          </div>
        )}

        {/* 사용자 카드 결과 */}
        {userCards.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">{t('dashboard.myBusinessCards', '사용자 카드')} ({userCards.length}개)</h2>
            <div className="space-y-4">
              {userCards.map((card) => (
                <div key={card.id} className="border rounded p-4">
                  <h3 className="font-semibold">{card.name}</h3>
                  <p className="text-sm text-gray-600">{card.email}</p>
                  <p className="text-sm text-gray-600">{card.company}</p>
                  <p className="text-sm text-gray-500">{t('customUrl.label', '커스텀 URL')}: {card.customUrl}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirebaseTestPage; 